/**
 * DealListsTab - Aba "Negócios" da Central de Importação de Listas
 *
 * Conteúdo extraído de src/pages/crm/CrmDealsImport.tsx sem alteração de
 * lógica, queries, filtros ou wizard. Apenas removido o wrapper externo
 * para integrar com o Hub.
 */

import { useState, useEffect, useMemo } from 'react';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Loader2,
  MoreHorizontal,
  PlayCircle,
  Activity,
  XCircle,
  Download,
  Trash2,
  Search,
  X,
  RefreshCw,
  FileSpreadsheet,
  Eye,
} from 'lucide-react';
import DealImportPreviewDialog, {
  type DealImportJobSummary,
} from '@/components/crm/import/DealImportPreviewDialog';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { ImportDealsWizard } from '@/components/crm/import/ImportDealsWizard';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDealImport } from '@/hooks/useDealImport';
import { useToast } from '@/hooks/use-toast';
import { downloadInvalidItemsCsv } from '@/lib/dealImportErrorsCsv';
import { createLogger } from '@/lib/logger';

import { useListExport } from '@/components/export/useListExport';

const logger = createLogger('DealListsTab');

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  validating: 'bg-blue-500/10 text-blue-600',
  awaiting_approval: 'bg-amber-500/10 text-amber-600',
  enqueued: 'bg-blue-500/10 text-blue-600',
  processing: 'bg-blue-500/10 text-blue-600',
  completed: 'bg-green-500/10 text-green-600',
  completed_with_errors: 'bg-amber-500/10 text-amber-600',
  failed: 'bg-destructive/10 text-destructive',
  cancelled: 'bg-muted text-muted-foreground',
};

// Cor da bolinha por status — padrão "dot + label preto"
const STATUS_DOT_COLORS: Record<string, string> = {
  draft: 'bg-muted-foreground',
  validating: 'bg-blue-500',
  awaiting_approval: 'bg-amber-500',
  enqueued: 'bg-blue-500',
  processing: 'bg-blue-500',
  completed: 'bg-green-500',
  completed_with_errors: 'bg-amber-500',
  failed: 'bg-destructive',
  cancelled: 'bg-muted-foreground',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  validating: 'Validando',
  awaiting_approval: 'Aguardando aprovação',
  enqueued: 'Na fila',
  processing: 'Processando',
  completed: 'Concluído',
  completed_with_errors: 'Concluído com erros',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

const CAN_RESUME_APPROVAL = new Set(['awaiting_approval']);
const CAN_VIEW_PROGRESS = new Set([
  'enqueued',
  'processing',
  'completed_with_errors',
  'failed',
]);
const CAN_CANCEL = new Set([
  'draft',
  'validating',
  'awaiting_approval',
  'enqueued',
  'processing',
]);
const CAN_DELETE = new Set([
  'cancelled',
  'failed',
  'completed_with_errors',
  'draft',
]);

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function DealListsTab() {
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  const tenantId = currentTenant?.id;
  const api = useDealImport();

  // Permissões granulares da aba Negócios (lists_deals)
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateJobs = canCreate('lists_deals');
  const canEditJobs = canEdit('lists_deals');
  const canDeleteJobs = canDelete('lists_deals');

  // Exportação da lista importada (módulo genérico de exportação de dados)
  const { podeExportar, exportarLista, dialogoExportacao } = useListExport('lists_deals');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [resume, setResume] = useState<{ jobId: string; step: 5 | 6 } | null>(null);
  // Abre wizard via ?new=1 (atalho "Nova lista de importação (Negócios)" do Quick Create).
  useAutoOpenFromQuery('1', () => setWizardOpen(true));

  const [confirmCancel, setConfirmCancel] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const [previewJob, setPreviewJob] = useState<DealImportJobSummary | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const jobsQuery = useQuery({
    queryKey: ['deal-import-jobs', tenantId, search, page, pageSize],
    enabled: !!tenantId,
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('list_deal_import_jobs', {
        p_search: search || null,
        p_statuses: null,
        p_page: page,
        p_page_size: pageSize,
      });
      if (error) {
        logger.error('Falha ao listar jobs de importação', { error: error.message });
        throw error;
      }
      return (data ?? []) as any[];
    },
  });

  const rows = jobsQuery.data ?? [];
  const totalCount = useMemo(
    () => (rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0),
    [rows],
  );

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  function openWizardForNew() {
    setResume(null);
    setWizardOpen(true);
  }

  function openWizardForResume(jobId: string, step: 5 | 6) {
    setResume({ jobId, step });
    setWizardOpen(true);
  }

  async function handleDownloadErrors(job: any) {
    try {
      const ok = await downloadInvalidItemsCsv(job.id, job.name);
      if (!ok) {
        toast({ title: 'Nenhum erro para exportar' });
      }
    } catch (err: any) {
      logger.error('Falha ao baixar CSV de erros', { jobId: job.id, error: err?.message });
      toast({
        title: 'Erro ao gerar CSV',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    }
  }

  async function handleConfirmCancel() {
    if (!confirmCancel) return;
    try {
      await api.cancelJob.mutateAsync(confirmCancel.id);
      toast({ title: 'Importação cancelada' });
    } catch (err: any) {
      toast({
        title: 'Erro ao cancelar',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    } finally {
      setConfirmCancel(null);
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    try {
      await api.deleteJob.mutateAsync(confirmDelete.id);
      toast({ title: 'Importação excluída' });
    } catch (err: any) {
      toast({
        title: 'Erro ao excluir',
        description: err?.message ?? 'Falha desconhecida',
        variant: 'destructive',
      });
    } finally {
      setConfirmDelete(null);
    }
  }

  return (
    <>
      {/* Barra de ações + filtros (sem header próprio — o Hub fornece o header global) */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b bg-muted/30">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          {searchInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchInput('')}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
          {jobsQuery.isFetching && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="xs"
            onClick={() => jobsQuery.refetch()}
            disabled={jobsQuery.isFetching}
          >
            <RefreshCw className={`mr-1.5 ${jobsQuery.isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          {canCreateJobs && (
            <Button size="xs" onClick={openWizardForNew}>
              <Plus className="mr-1.5" />
              Nova importação
            </Button>
          )}
        </div>
      </div>

      <div className="p-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Importações de Negócios
            </CardTitle>
            <CardDescription>
              Jobs de importação de negócios criados neste tenant
            </CardDescription>
          </CardHeader>
          <CardContent>
            {jobsQuery.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Nenhuma importação encontrada</p>
                <p className="text-sm">
                  {search
                    ? 'Ajuste a busca ou limpe o filtro.'
                    : 'Clique em "Nova importação" para começar.'}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Na fila</TableHead>
                      <TableHead className="text-right">Sucesso</TableHead>
                      <TableHead
                        className="text-right"
                        title="Linhas rejeitadas na validação (inválidas, duplicadas e bloqueadas)"
                      >
                        Inválidos
                      </TableHead>
                      <TableHead
                        className="text-right"
                        title="Falhas durante o processamento pelo worker"
                      >
                        Falhas
                      </TableHead>
                      <TableHead>Criado em</TableHead>
                      <TableHead className="w-16 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((j) => {
                      const canResume = CAN_RESUME_APPROVAL.has(j.status) && canEditJobs;
                      const canView = CAN_VIEW_PROGRESS.has(j.status);
                      const canCancel = CAN_CANCEL.has(j.status) && canEditJobs;
                      const canDelete = CAN_DELETE.has(j.status) && canDeleteJobs;
                      const invalidTotal =
                        (j.invalid_count ?? 0) +
                        (j.duplicate_count ?? 0) +
                        (j.blacklisted_count ?? 0);
                      const workerFailures = j.failed_items ?? 0;
                      const totalFailures = invalidTotal + workerFailures;
                      const hasErrors = totalFailures > 0;
                      const queuedItems = Math.max(
                        0,
                        (j.total_rows ?? 0) - (j.succeeded_items ?? 0) - totalFailures,
                      );

                      return (
                        <TableRow key={j.id}>
                          <TableCell className="font-medium">{j.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-foreground">
                              <span className={cn('h-2 w-2 rounded-full', STATUS_DOT_COLORS[j.status] || 'bg-muted-foreground')} />
                              {STATUS_LABELS[j.status] || j.status}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {j.total_rows?.toLocaleString('pt-BR') ?? 0}
                          </TableCell>
                          <TableCell className="text-right text-blue-600">
                            {queuedItems.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {j.succeeded_items?.toLocaleString('pt-BR') ?? 0}
                          </TableCell>
                          <TableCell className="text-right text-amber-600">
                            {invalidTotal.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right text-destructive">
                            {workerFailures.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {j.created_at &&
                              format(new Date(j.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Ações</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                {(j.total_rows ?? 0) > 0 && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setPreviewJob({
                                        id: j.id,
                                        name: j.name,
                                        created_at: j.created_at,
                                        total_rows: j.total_rows,
                                        succeeded_items: j.succeeded_items,
                                        failed_items: j.failed_items,
                                        invalid_count: j.invalid_count,
                                        duplicate_count: j.duplicate_count,
                                        blacklisted_count: j.blacklisted_count,
                                      })
                                    }
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Visualizar amostra
                                  </DropdownMenuItem>
                                )}
                                {canResume && (
                                  <DropdownMenuItem onClick={() => openWizardForResume(j.id, 5)}>
                                    <PlayCircle className="h-4 w-4 mr-2" />
                                    Continuar aprovação
                                  </DropdownMenuItem>
                                )}
                                {canView && (
                                  <DropdownMenuItem onClick={() => openWizardForResume(j.id, 6)}>
                                    <Activity className="h-4 w-4 mr-2" />
                                    Ver progresso
                                  </DropdownMenuItem>
                                )}
                                {hasErrors && (
                                  <DropdownMenuItem onClick={() => handleDownloadErrors(j)}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Baixar CSV de erros
                                  </DropdownMenuItem>
                                )}
                                {podeExportar && (j.total_rows ?? 0) > 0 && (
                                  <DropdownMenuItem
                                    onClick={() => exportarLista({ refId: j.id, name: j.name })}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Exportar lista
                                  </DropdownMenuItem>
                                )}
                                {(canResume || canView || hasErrors) &&
                                  (canCancel || canDelete) && <DropdownMenuSeparator />}
                                {canCancel && (
                                  <DropdownMenuItem
                                    onClick={() => setConfirmCancel({ id: j.id, name: j.name })}
                                    className="text-amber-600 focus:text-amber-600"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Cancelar
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <DropdownMenuItem
                                    onClick={() => setConfirmDelete({ id: j.id, name: j.name })}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Excluir
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <div className="border-t mt-2 px-4 py-2 bg-background">
                  <PaginationFooter
                    totalItems={totalCount}
                    currentPage={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    borderClassName=""
                    className="mt-0 pt-0"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <ImportDealsWizard
        open={wizardOpen}
        onOpenChange={(v) => {
          setWizardOpen(v);
          if (!v) setResume(null);
        }}
        resume={resume}
        onComplete={() => jobsQuery.refetch()}
      />

      <AlertDialog open={!!confirmCancel} onOpenChange={(v) => !v && setConfirmCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar importação?</AlertDialogTitle>
            <AlertDialogDescription>
              A importação <strong>{confirmCancel?.name}</strong> será marcada como cancelada.
              Batches já em processamento podem finalizar normalmente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel} disabled={api.cancelJob.isPending}>
              {api.cancelJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cancelar importação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir importação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove permanentemente o job <strong>{confirmDelete?.name}</strong> e
              todos os seus itens. Os negócios já criados no CRM <strong>não</strong> serão
              afetados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={api.deleteJob.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {api.deleteJob.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {previewJob && (
        <DealImportPreviewDialog
          job={previewJob}
          open={!!previewJob}
          onOpenChange={(o) => !o && setPreviewJob(null)}
        />
      )}

      {/* Modal genérico de exportação da lista selecionada */}
      {dialogoExportacao}
    </>
  );
}
